import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Grid, Avatar, IconButton, Divider,
  CircularProgress, Alert,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PremiumDatePicker from "../../Components/PremiumDatePicker";
import { updateLead, type LeadThread } from "./leadApi";
import {
  leadFormFields, leadFormSectionLabel,
  splitLeadFormValues, loadLeadFormValues,
} from "./leadVertical";

const T = {
  navy: "#16233A", gold: "#7C4DFF",
  white: "#fff", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{10}$/;

const stripNonDigits = (p: string) => p.replace(/\D/g, "");

function validatePhone(p: string): string | undefined {
  if (!p) return undefined;
  if (p.length !== 10 || !PHONE_RE.test(p)) return "Phone must be exactly 10 digits";
  return undefined;
}

function validateEmail(e: string): string | undefined {
  if (!e) return undefined;
  if (!EMAIL_RE.test(e)) return "Enter a valid email address";
  return undefined;
}

interface FormState {
  name: string; email: string; phone: string; organization: string;
  /** Vertical-driven field values keyed by `column:<name>` / `custom:<slug>`. */
  vertical: Record<string, string>;
}

interface FieldErrors { name?: string; email?: string; phone?: string }

function leadToForm(lead: LeadThread): FormState {
  return {
    name:         lead.name ?? "",
    email:        lead.email ?? "",
    phone:        lead.phone ?? "",
    organization: lead.organization ?? "",
    vertical:     loadLeadFormValues(lead),
  };
}

interface Props {
  open: boolean;
  lead: LeadThread;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditLeadDialog({ open, lead, onClose, onSaved }: Props) {
  const initial = useMemo(() => leadToForm(lead), [lead]);
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setErrors({});
      setSaveError(null);
    }
  }, [open, initial]);

  const set = (field: "name" | "email" | "phone" | "organization") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let val = e.target.value;
      if (field === "phone") {
        val = stripNonDigits(val).slice(0, 10);
        setForm((p) => ({ ...p, phone: val }));
        setErrors((p) => ({ ...p, phone: validatePhone(val) }));
        return;
      }
      if (field === "email") {
        val = val.trimStart().toLowerCase();
        setForm((p) => ({ ...p, email: val }));
        setErrors((p) => ({ ...p, email: validateEmail(val.trim()) }));
        return;
      }
      setForm((p) => ({ ...p, [field]: val }));
    };

  /**
   * Compute the changed-field payload. For contact fields we diff on the
   * base FormState; for vertical fields (columns + custom_fields) we
   * split the current vertical map and emit only the entries that
   * differ from the initial split. Autism / tutoring never emit venue
   * columns here because their vertical map contains only `custom:*`
   * keys — the columns bag always comes back empty.
   */
  const dirty = useMemo(() => {
    const out: Record<string, any> = {};
    if (form.name.trim() !== initial.name)                                     out.name = form.name.trim();
    if (form.email.trim() !== initial.email)                                   out.email = form.email.trim() || null;
    if (form.phone !== initial.phone)                                          out.phone = form.phone || null;
    if (form.organization !== initial.organization)                            out.organization = form.organization || null;

    const cur  = splitLeadFormValues(form.vertical);
    const prev = splitLeadFormValues(initial.vertical);
    for (const [k, v] of Object.entries(cur.columns)) {
      if (prev.columns[k] !== v) out[k] = v;
    }
    // Blank-out columns removed since initial (present in prev, absent now).
    for (const k of Object.keys(prev.columns)) {
      if (!(k in cur.columns) && !(k in out)) out[k] = null;
    }
    // Custom fields: only emit the full custom_fields object when any
    // custom entry changed. Backend upsert merges by slug — sending the
    // current state overwrites just the supplied keys.
    const cfChanged = JSON.stringify(cur.customFields) !== JSON.stringify(prev.customFields);
    if (cfChanged && Object.keys(cur.customFields).length) {
      out.custom_fields = cur.customFields;
    }
    return out;
  }, [form, initial]);

  const hasChanges = Object.keys(dirty).length > 0;
  const hasErrors = Object.values(errors).some(Boolean);
  const nameInvalid = !form.name.trim();

  const handleSubmit = async () => {
    if (nameInvalid || hasErrors || !hasChanges) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateLead(lead.id, dirty);
      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.message ||
        (err?.errors ? Object.values(err.errors).flat().join(" ") : null) ||
        "Failed to save changes. Please try again.";
      setSaveError(msg);
    }
    setSaving(false);
  };

  const nameInitial = form.name.trim() ? form.name.trim()[0].toUpperCase() : "?";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: 13.5, borderRadius: "6px",
      "& fieldset": { borderColor: "#dde1e8" },
      "&:hover fieldset": { borderColor: "#94a3b8" },
      "&.Mui-focused fieldset": { borderColor: T.navy, borderWidth: 1.5 },
    },
    "& .MuiInputLabel-root": { fontSize: 13 },
    "& .MuiFormHelperText-root": { fontSize: 11, mt: "3px" },
  };

  const sectionLabel = (text: string) => (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: T.slateLt,
      textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5,
    }}>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px", overflow: "hidden",
          boxShadow: "0 20px 60px rgba(22,35,58,0.2)",
        },
      }}
    >
      <Box sx={{
        background: `linear-gradient(135deg, ${T.navy} 0%, #1a3354 100%)`,
        px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
      }}>
        <Avatar sx={{
          width: 44, height: 44, bgcolor: T.gold,
          fontSize: 18, fontWeight: 700, color: T.navy,
          boxShadow: "0 4px 12px rgba(124,77,255,0.35)",
        }}>
          {nameInitial}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>
            Edit Lead
          </Typography>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)", mt: 0.2 }}>
            {lead.lead_number ? `${lead.lead_number} · ` : ""}Changes are logged to this lead's history
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}
          sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: T.white, bgcolor: "rgba(255,255,255,0.08)" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        {saveError && (
          <Box sx={{ px: 3, pt: 2.5 }}>
            <Alert severity="error" sx={{ borderRadius: "8px", fontSize: "0.78rem", py: 0.75 }}>
              {saveError}
            </Alert>
          </Box>
        )}

        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {sectionLabel("Contact Information")}

          <TextField
            label="Full Name *"
            size="small"
            fullWidth
            value={form.name}
            onChange={set("name")}
            error={nameInvalid}
            helperText={nameInvalid ? "Name is required" : undefined}
            sx={{ ...fieldSx, mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Email Address"
                size="small"
                fullWidth
                value={form.email}
                onChange={set("email")}
                error={!!errors.email}
                helperText={errors.email}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone Number"
                size="small"
                fullWidth
                value={form.phone}
                onChange={set("phone")}
                error={!!errors.phone}
                helperText={errors.phone || (form.phone ? `${form.phone.length} digits` : undefined)}
                inputProps={{ inputMode: "numeric" }}
                sx={fieldSx}
              />
            </Grid>
          </Grid>

          <TextField
            label="Organization"
            size="small"
            fullWidth
            value={form.organization}
            onChange={set("organization")}
            sx={{ ...fieldSx, mt: 2 }}
          />
        </Box>

        <Divider sx={{ borderColor: T.line }} />

        {/* Vertical-driven inquiry section — autism / tutoring see program /
            child / location fields (persisted to custom_fields); venue
            sees event type / date / guests / budget / hall. See
            leadVertical.leadFormFields. */}
        <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
          {sectionLabel(leadFormSectionLabel)}

          <Grid container spacing={2}>
            {leadFormFields.map((f) => {
              const value = form.vertical[f.key] ?? "";
              const onChange = (val: string) =>
                setForm((p) => ({ ...p, vertical: { ...p.vertical, [f.key]: val } }));
              const cols = f.gridColumns ?? 6;
              if (f.type === "select") {
                return (
                  <Grid key={f.key} item xs={cols}>
                    <FormControl size="small" fullWidth sx={fieldSx}>
                      <InputLabel sx={{ fontSize: 13 }}>{f.label}</InputLabel>
                      <Select
                        value={value}
                        label={f.label}
                        onChange={(e) => onChange(String(e.target.value))}
                        sx={{ fontSize: 13.5 }}
                      >
                        <MenuItem value="" sx={{ fontSize: 13.5, color: T.slateLt }}>
                          <em>None</em>
                        </MenuItem>
                        {(f.options ?? []).map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13.5 }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              }
              if (f.type === "date") {
                return (
                  <Grid key={f.key} item xs={cols}>
                    <PremiumDatePicker
                      label={f.label}
                      value={value}
                      onChange={(val) => onChange(val)}
                      size="small"
                    />
                  </Grid>
                );
              }
              return (
                <Grid key={f.key} item xs={cols}>
                  <TextField
                    label={f.label}
                    type={f.type === "number" ? "number" : "text"}
                    multiline={f.type === "textarea"}
                    rows={f.type === "textarea" ? 3 : undefined}
                    size="small"
                    fullWidth
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={f.placeholder}
                    inputProps={f.type === "number" ? { min: 0 } : undefined}
                    sx={fieldSx}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3, py: 2,
        bgcolor: "#f8f9fb",
        borderTop: `1px solid ${T.line}`,
        gap: 1,
        justifyContent: "space-between",
      }}>
        <Typography sx={{ fontSize: 11.5, color: T.slateLt, display: "flex", alignItems: "center", gap: 0.6 }}>
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
          {hasChanges
            ? `${Object.keys(dirty).length} field${Object.keys(dirty).length === 1 ? "" : "s"} changed`
            : "No changes"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: 13,
              color: T.slate, px: 2.5, borderRadius: "6px",
              "&:hover": { bgcolor: "#eef0f4" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || nameInvalid || hasErrors || !hasChanges}
            startIcon={saving
              ? <CircularProgress size={15} sx={{ color: T.white }} />
              : <SaveOutlinedIcon sx={{ fontSize: 17 }} />
            }
            sx={{
              textTransform: "none", fontWeight: 700, fontSize: 13,
              px: 3, borderRadius: "6px",
              bgcolor: T.navy,
              boxShadow: "0 2px 8px rgba(22,35,58,0.2)",
              "&:hover": { bgcolor: "#1a3354", boxShadow: "0 4px 14px rgba(22,35,58,0.3)" },
              "&.Mui-disabled": { bgcolor: "#c5cdd8", color: T.white },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
